import React from 'react';
import { useSearchParams } from 'react-router-dom';
import classNames from 'classnames/bind';

import { validatePageParam } from '@buyside/util/numbers';

import styles from './indexPaginationNavStyles.scss';

const cx = classNames.bind(styles);

type IndexPaginationNavProps = {
  customScroll?: () => void;
  resultCount: number;
  resultLength: number;
  loading?: boolean;
  resultsPerPage: number;
};

export const IndexPaginationNav: React.FC<IndexPaginationNavProps> = ({
  customScroll,
  resultCount,
  resultLength,
  loading,
  resultsPerPage
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const currentPage = validatePageParam(searchParams);

  if ((loading && !resultCount) || resultLength < 1) return null;

  const pagesCount = Math.ceil((resultCount > 10000 ? 10000 : resultCount) / resultsPerPage);

  const getPages = (): number[] => {
    const pages = [currentPage];
    let page = currentPage;
    let pageOffset = 1;
    while (pages.length < pagesCount && pages.length < 5) {
      page = currentPage + pageOffset;
      if (page > 0 && page <= pagesCount) pages.push(page);
      page = currentPage - pageOffset;
      if (page > 0 && page <= pagesCount) pages.push(page);
      pageOffset++;
    }
    return pages.sort((a, b) => a - b);
  };

  const firstNavButtonsStyles = cx({
    navButton: true,
    disabled: currentPage === 1
  });
  const lastNavButtonsStyles = cx({
    navButton: true,
    disabled: currentPage === pagesCount
  });

  const navigateAndScroll = (targetPage: number) => (): void => {
    if (targetPage === currentPage) return;

    setSearchParams(prev => {
      prev.set('page', `${targetPage}`);
      return prev;
    });
    if (customScroll) {
      customScroll();
    } else {
      window.scrollTo(0, 0);
    }
  };

  return (
    <nav className={styles.paginationArea}>
      <button className={firstNavButtonsStyles} onClick={navigateAndScroll(1)}>
        First
      </button>
      <button className={firstNavButtonsStyles} onClick={navigateAndScroll(currentPage - 1)}>
        Previous
      </button>
      {getPages().map((page, idx) => {
        if (page === currentPage) {
          return (
            <span className={`${styles.navButton} ${styles.currentPage}`} key={`${page}-${idx}`}>
              {currentPage}
            </span>
          );
        } else {
          return (
            <button
              key={`${page}-${idx}`}
              className={styles.navButton}
              onClick={navigateAndScroll(page)}
            >
              {page}
            </button>
          );
        }
      })}
      <button className={lastNavButtonsStyles} onClick={navigateAndScroll(currentPage + 1)}>
        Next
      </button>
      <button className={lastNavButtonsStyles} onClick={navigateAndScroll(pagesCount)}>
        Last
      </button>
    </nav>
  );
};

export default IndexPaginationNav;
